import {
  STEP_UP_INTERACTION_REQUIRED,
  STEP_UP_OAUTH_CONFIG,
  STEP_UP_PROMPT_NONE,
} from './constants';
import { generatePkce } from './pkce';
import { StepUpCancelledError, StepUpFailedError } from './stepUpError';
import type { StepUpInteractiveHost, StepUpRequiredDetails } from './types';
import type { AuthorizeUrlConfig } from './webMessageAuthorize';
import {
  authorizeEndpointOrigin,
  buildAuthorizeUrl,
  readWebMessage,
} from './webMessageAuthorize';

export type StepUpIframeConfig = AuthorizeUrlConfig;

export const DEFAULT_STEP_UP_IFRAME_CONFIG: StepUpIframeConfig = {
  authorizeUrl: STEP_UP_OAUTH_CONFIG.AUTHORIZE_URL,
  redirectUri: STEP_UP_OAUTH_CONFIG.REDIRECT_URI,
  clientId: STEP_UP_OAUTH_CONFIG.CLIENT_ID,
  scope: STEP_UP_OAUTH_CONFIG.SCOPE,
};

export interface StepUpIframeResult {
  code: string;
  codeVerifier: string;
}

type Settlers = {
  resolve: (code: string) => void;
  reject: (error: Error) => void;
};

type Phase = 'silent' | 'interactive';

// Gets an /authorize code: silently in a hidden iframe (prompt=none) first, and only on
// interaction_required asks the host to show its modal. Renders no UI itself.
export class StepUpIframe {
  private readonly host: StepUpInteractiveHost;
  private readonly config: StepUpIframeConfig;

  private state: string | null = null;
  private phase: Phase = 'silent';
  private settled = false;
  private settlers: Settlers | null = null;

  private silentFrame: HTMLIFrameElement | null = null;
  private details: StepUpRequiredDetails | null = null;
  private codeChallenge: string | null = null;
  private timeoutId: ReturnType<typeof setTimeout> | null = null;
  private silentTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(
    host: StepUpInteractiveHost,
    config: StepUpIframeConfig = DEFAULT_STEP_UP_IFRAME_CONFIG,
  ) {
    this.host = host;
    this.config = config;
  }

  async run(details: StepUpRequiredDetails): Promise<StepUpIframeResult> {
    const authorization = this.open();
    void authorization.catch(() => undefined);
    try {
      const { codeVerifier, codeChallenge } = await generatePkce();
      this.authorize(details, codeChallenge);
      const code = await authorization;
      return { code, codeVerifier };
    } catch (error) {
      this.close();
      throw error;
    }
  }

  close(): void {
    this.settle(({ reject }) => reject(new StepUpCancelledError()));
  }

  // Synchronous so a close() during the async PKCE step still has a pending flow to
  // reject. authorize() then starts the silent attempt.
  private open(): Promise<string> {
    this.settle(({ reject }) => reject(new StepUpCancelledError()));
    this.state = crypto.randomUUID();
    this.phase = 'silent';
    this.settled = false;

    return new Promise<string>((resolve, reject) => {
      this.settlers = { resolve, reject };

      if (!this.config.authorizeUrl || !this.config.clientId) {
        this.settle(({ reject: rej }) =>
          rej(
            new StepUpFailedError(
              undefined,
              'Step-up OAuth is not configured (authorizeUrl / clientId missing)',
            ),
          ),
        );
        return;
      }

      this.silentFrame = this.createHiddenFrame();
      document.body.appendChild(this.silentFrame);
      window.addEventListener('message', this.handleMessage);
      this.timeoutId = setTimeout(
        () => this.settle(({ reject: rej }) => rej(new StepUpCancelledError())),
        STEP_UP_OAUTH_CONFIG.TIMEOUT_MS,
      );
    });
  }

  private authorize(
    details: StepUpRequiredDetails,
    codeChallenge: string,
  ): void {
    if (this.settled || !this.silentFrame || !this.state) {
      return;
    }
    this.details = details;
    this.codeChallenge = codeChallenge;
    this.silentTimeoutId = setTimeout(() => {
      if (this.phase === 'silent') {
        this.settle(({ reject }) =>
          reject(new StepUpFailedError(undefined, 'Silent step-up timed out')),
        );
      }
    }, STEP_UP_OAUTH_CONFIG.SILENT_TIMEOUT_MS);
    this.silentFrame.src = this.authorizeUrl(true);
  }

  private handleMessage = (event: MessageEvent): void => {
    const outcome = readWebMessage(event, this.expectedOrigin, this.state);
    if (!outcome) {
      return;
    }
    if (outcome.kind === 'error') {
      if (
        this.phase === 'silent' &&
        outcome.error === STEP_UP_INTERACTION_REQUIRED
      ) {
        this.goInteractive();
        return;
      }
      this.settle(({ reject }) => reject(new StepUpFailedError(outcome.error)));
      return;
    }
    this.settle(({ resolve }) => resolve(outcome.code));
  };

  // Same state + PKCE challenge as the silent attempt, so the listener and the
  // caller's verifier still apply to the code the host's iframe posts back.
  private goInteractive(): void {
    if (this.settled || this.phase === 'interactive') {
      return;
    }
    this.phase = 'interactive';
    this.clearSilentTimeout();
    this.removeSilentFrame();
    this.host.open(this.authorizeUrl(false), () => this.close());
  }

  private authorizeUrl(silent: boolean): string {
    return buildAuthorizeUrl(this.config, {
      state: this.state as string,
      details: this.details as StepUpRequiredDetails,
      codeChallenge: this.codeChallenge as string,
      prompt: silent ? STEP_UP_PROMPT_NONE : undefined,
    });
  }

  private get expectedOrigin(): string {
    return authorizeEndpointOrigin(this.config.authorizeUrl);
  }

  private createHiddenFrame(): HTMLIFrameElement {
    const frame = document.createElement('iframe');
    frame.setAttribute('aria-hidden', 'true');
    frame.style.cssText =
      'position:fixed;left:-99999px;top:0;width:1px;height:1px;border:0;';
    return frame;
  }

  private settle(action: (settlers: Settlers) => void): void {
    if (this.settled || !this.settlers) {
      this.teardown();
      return;
    }
    this.settled = true;
    const settlers = this.settlers;
    this.teardown();
    action(settlers);
  }

  private teardown(): void {
    window.removeEventListener('message', this.handleMessage);
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    this.clearSilentTimeout();
    this.removeSilentFrame();
    this.host.close();
    this.settlers = null;
  }

  private removeSilentFrame(): void {
    this.silentFrame?.parentNode?.removeChild(this.silentFrame);
    this.silentFrame = null;
  }

  private clearSilentTimeout(): void {
    if (this.silentTimeoutId !== null) {
      clearTimeout(this.silentTimeoutId);
      this.silentTimeoutId = null;
    }
  }
}
